import { useEffect, useRef, useState } from 'react'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { ApiError } from '../../../api/problem'
import { inviteUser, listProfiles, resendInvite } from '../../../api/userAdmin'
import type { ProfileSummary } from '../../../types/userAdmin'

export interface InviteUserDialogProps {
  onClose: () => void
  /** Called once the invitee accepts and becomes a real `AppUser` row — see this file's own
   * note: Slice B ships no `accept()` HTTP surface, so this never fires from THIS dialog; the
   * admin instead refreshes the list later once the invitee has accepted out-of-band. */
  onInvited: () => void
}

/**
 * `POST /api/v1/users` (invite) — user-admin-entra-federation Slice B (design §2.2, AC-B1).
 * Assigning Permission Sets/Role/Territory happens AFTER invite, via the per-user assignment
 * editor (`UserAssignmentDialog`) once the invitee has accepted — keeping this dialog to the one
 * thing `InviteUserRequest` needs up front: email, name, and the floor Profile.
 *
 * <p><b>Honest scope note (Slice B ships no `accept()` HTTP surface):</b> after invite, the
 * invitee is still a {@code pending UserInvitation}, not yet a listed {@code AppUser} — so this
 * dialog cannot show them landing in the Users roster immediately. It instead confirms the invite
 * succeeded and offers "Resend invite" (using the invitation id returned right here — the only
 * moment this id is available client-side, since there is no list-pending-invitations endpoint).
 */
export function InviteUserDialog({ onClose, onInvited }: InviteUserDialogProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [profileKey, setProfileKey] = useState('')
  const [profiles, setProfiles] = useState<ProfileSummary[]>([])
  const [profilesError, setProfilesError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invitedId, setInvitedId] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const controller = new AbortController()
    listProfiles(controller.signal)
      .then(setProfiles)
      .catch((err) => {
        if (err instanceof ApiError) setProfilesError(err.problem.detail ?? err.message)
        else setProfilesError('Could not load the Profile catalog.')
      })
    return () => controller.abort()
  }, [])

  const isValid = email.trim() !== '' && name.trim() !== '' && profileKey !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const invitation = await inviteUser({
        email: email.trim(),
        name: name.trim(),
        profileKey,
        permissionSetKeys: [],
        roleId: null,
        territoryIds: [],
      })
      setInvitedId(invitation.invitationId)
    } catch (err) {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
      else setError('Something went wrong sending this invitation. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    if (!invitedId || resending) return
    setResending(true)
    setError(null)
    try {
      await resendInvite(invitedId)
      setResent(true)
    } catch (err) {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
      else setError('Could not resend the invitation. Please try again.')
    } finally {
      setResending(false)
    }
  }

  if (invitedId) {
    return (
      <FocusTrapDialog
        titleId="invite-user-sent-title"
        title="Invitation sent"
        onClose={() => {
          onClose()
          onInvited()
        }}
        footer={
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            onClick={() => {
              onClose()
              onInvited()
            }}
          >
            Done
          </button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
          {resent ? <InlineAlert severity="success">Invitation re-sent to {email.trim()}.</InlineAlert> : null}
          <p style={{ margin: 0 }}>
            An invitation was sent to <strong>{email.trim()}</strong>. Assign Permission Sets, a Role, or
            Territories once they accept and appear in the Users list.
          </p>
          <div>
            <button type="button" className="iw-btn" onClick={() => void handleResend()} disabled={resending}>
              {resending ? 'Resending…' : 'Resend invite'}
            </button>
          </div>
        </div>
      </FocusTrapDialog>
    )
  }

  return (
    <FocusTrapDialog
      titleId="invite-user-title"
      title="Invite user"
      onClose={onClose}
      initialFocusRef={emailRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            onClick={() => void handleSubmit()}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Sending…' : 'Send invitation'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        {profilesError ? <InlineAlert severity="warning">{profilesError}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="invite-email">
            Email
          </label>
          <input
            id="invite-email"
            ref={emailRef}
            type="email"
            className="iw-input"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="invite-name">
            Name
          </label>
          <input
            id="invite-name"
            className="iw-input"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="invite-profile">
            Profile
          </label>
          <select
            id="invite-profile"
            className="iw-select"
            value={profileKey}
            onChange={(e) => setProfileKey(e.target.value)}
            required
          >
            <option value="" disabled>
              Select…
            </option>
            {profiles.map((p) => (
              <option key={p.id} value={p.key}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </form>
    </FocusTrapDialog>
  )
}
